// src/components/statistics/UserStatsBar.tsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { statisticsService, UserRanking } from "../../services/statisticsService";
import "./UserStatsBar.css";

/**
 * A thin motivational stats bar rendered just below the header.
 * Only visible when the user is authenticated.
 * Clicking anywhere on the bar navigates to the statistics page.
 *
 * Shows four metrics pulled from GET /api/statistics/my-ranking:
 *   - Total entry count
 *   - Global entry rank (#1 = most entries)
 *   - Total karma
 *   - Global karma rank (#1 = highest karma)
 */
export const UserStatsBar: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [ranking, setRanking] = useState<UserRanking | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setRanking(null);
      return;
    }

    statisticsService
      .getUserRanking()
      .then(setRanking)
      .catch(() => {
        // Silently fail — bar simply stays hidden on error
        setRanking(null);
      });
  }, [isAuthenticated]);

  // Guests never see the bar.
  if (!isAuthenticated) return null;

  // While the ranking request is in flight, render a same-height skeleton so the bar
  // reserves its space and fills in without pushing the page content down. This removes
  // the layout jump that happened when the (slow) ranking query finally returned and the
  // bar suddenly appeared from zero height.
  if (!ranking) {
    return (
      <div className="user-stats-bar user-stats-bar--loading" aria-hidden="true">
        <span className="user-stats-bar__skeleton" />
        <div className="user-stats-bar__divider" aria-hidden="true" />
        <span className="user-stats-bar__skeleton" />
        <div className="user-stats-bar__divider" aria-hidden="true" />
        <span className="user-stats-bar__skeleton" />
        <div className="user-stats-bar__divider" aria-hidden="true" />
        <span className="user-stats-bar__skeleton" />
      </div>
    );
  }

  const fmt = (n: number) => n.toLocaleString("tr-TR");

  return (
    <div
      className="user-stats-bar"
      onClick={() => navigate("/istatistikler")}
      title="İstatistiklere git"
    >
      {/* Entry count metric */}
      <div className="user-stats-bar__metric">
        <span className="user-stats-bar__icon">✍️</span>
        <span className="user-stats-bar__value">{fmt(ranking.entryCount)}</span>
        <span className="user-stats-bar__label">tanım</span>
      </div>

      <div className="user-stats-bar__divider" aria-hidden="true" />

      {/* Entry rank metric */}
      <div className="user-stats-bar__metric">
        <span className="user-stats-bar__icon">🏆</span>
        <span className="user-stats-bar__rank">#{fmt(ranking.entryRank)}</span>
        <span className="user-stats-bar__label">/ {fmt(ranking.totalUsers)} tanım sırası</span>
      </div>

      <div className="user-stats-bar__divider" aria-hidden="true" />

      {/* Karma metric */}
      <div className="user-stats-bar__metric">
        <span className="user-stats-bar__icon">⚡</span>
        <span className={`user-stats-bar__value${ranking.karma < 0 ? " user-stats-bar__value--negative" : ""}`}>
          {ranking.karma > 0 ? `+${fmt(ranking.karma)}` : fmt(ranking.karma)}
        </span>
        <span className="user-stats-bar__label">karma</span>
      </div>

      <div className="user-stats-bar__divider" aria-hidden="true" />

      {/* Karma rank metric */}
      <div className="user-stats-bar__metric">
        <span className="user-stats-bar__icon">⭐</span>
        <span className="user-stats-bar__rank">#{fmt(ranking.karmaRank)}</span>
        <span className="user-stats-bar__label">/ {fmt(ranking.totalUsers)} karma sırası</span>
      </div>

    
    </div>
  );
};